import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

import { setLenis } from "@/lib/lenis";

const prefersReduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Global smooth-scroll + scroll-trigger setup.
 *
 * GSAP/ScrollTrigger and Lenis are loaded with dynamic import() inside the
 * effect so they stay out of the initial bundle, and are skipped entirely
 * under prefers-reduced-motion (everything renders static). See the header
 * docblock in the prior version for the full behavior contract.
 */
export function MotionProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  useEffect(() => {
    if (prefersReduced()) {
      setLenis(null);
      // Still clear any triggers on nav without loading GSAP.
      return;
    }

    let cancelled = false;
    let lenis:
      | {
          destroy: () => void;
          raf: (t: number) => void;
          on: (e: string, fn: () => void) => void;
          off: (e: string, fn: () => void) => void;
          scrollTo: (t: number, o?: { immediate?: boolean }) => void;
          stop: () => void;
          start: () => void;
        }
      | undefined;
    let onTick: ((time: number) => void) | undefined;
    let onLenisScroll: (() => void) | undefined;
    let unsub: (() => void) | undefined;
    let ScrollTriggerRef: any;

    (async () => {
      if (cancelled) return;
      const [{ default: gsap }, { ScrollTrigger }, { default: Lenis }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
        import("lenis"),
      ]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      ScrollTriggerRef = ScrollTrigger;

      const inst = new Lenis({ duration: 1.05, smoothWheel: true, touchMultiplier: 1.4 });
      lenis = inst as any;
      setLenis(inst as any);

      onLenisScroll = () => ScrollTrigger.update();
      inst.on("scroll", onLenisScroll);

      // The ONLY rAF driver — no second requestAnimationFrame loop.
      onTick = (time: number) => inst.raf(time * 1000);
      gsap.ticker.add(onTick);
      gsap.ticker.lagSmoothing(0);

      ScrollTrigger.refresh();

      const onResolved = () => {
        inst.scrollTo(0, { immediate: true });
        ScrollTrigger.refresh();
      };
      unsub = router.subscribe("onLoad", onResolved) as any;
    })();

    return () => {
      cancelled = true;
      if (onTick) {
        // gsap is loaded here only if the async block completed; re-import to
        // get the same singleton for ticker removal.
        import("gsap").then(({ default: gsap }) => gsap.ticker.remove(onTick!)).catch(() => {});
      }
      if (lenis && onLenisScroll) lenis.off("scroll", onLenisScroll);
      lenis?.destroy();
      setLenis(null);
      unsub?.();
      if (ScrollTriggerRef) ScrollTriggerRef.getAll().forEach((t: any) => t.kill());
    };
  }, [router]);

  return <>{children}</>;
}
